import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Send,
  Sparkles,
  Loader2,
  Trash2,
  Plus,
  MessagesSquare,
  Heart,
  Compass,
  CalendarRange,
  X,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../lib/authContext";
import { assistantApi } from "../lib/assistantApi";
import { formatApiError } from "../lib/authApi";
import ShareButton from "../features/share/ShareButton";
import { BibleRefLink } from "../lib/bibleRef";

const SUGGESTIONS = [
  { text: "Estou ansioso, o que a Bíblia diz?" },
  { text: "Me explique João 3:16" },
  { text: "Preciso de um versículo de força" },
  { text: "O que a Bíblia fala sobre perdão?" },
];

const MODES = [
  {
    key: "default",
    label: "Conversar",
    Icon: Sparkles,
    blurb:
      "Pergunte qualquer coisa sobre a Bíblia, doutrina ou aplicação.",
  },
  {
    key: "counselor",
    label: "Conselheiro",
    Icon: Heart,
    blurb:
      "Compartilhe como está se sentindo. Receba acolhimento + versículo + oração.",
  },
  {
    key: "mentor",
    label: "Mentor",
    Icon: Compass,
    blurb:
      "Direção para sua caminhada: próximas leituras, sermões e disciplinas para a semana.",
  },
];

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

export default function Assistente() {
  const { isAuthenticated, isChecking, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const starter = new URLSearchParams(location.search).get("starter") || "";

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {id, role, content, created_at}
  const [usage, setUsage] = useState(null); // {used_today, daily_limit, date_key}
  const [sessions, setSessions] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [mode, setMode] = useState("default");
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const scrollerRef = useRef(null);

  // Pre-fill the input when navigated with ?starter=...
  useEffect(() => {
    if (starter && isAuthenticated) setInput(starter);
  }, [starter, isAuthenticated]);

  // Auto-scroll on new message
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  // Initial load (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const [u, s] = await Promise.all([
          assistantApi.usage(),
          assistantApi.listSessions(),
        ]);
        if (cancelled) return;
        setUsage(u);
        setSessions(s);
      } catch {
        if (!cancelled) toast.error("Não foi possível carregar suas conversas.");
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const startNew = () => {
    setSessionId(null);
    setMessages([]);
    setShowHistory(false);
  };

  const openSession = async (id) => {
    try {
      const msgs = await assistantApi.listMessages(id);
      setSessionId(id);
      setMessages(msgs);
      setShowHistory(false);
    } catch {
      toast.error("Não foi possível abrir a conversa.");
    }
  };

  const removeSession = async (id) => {
    if (!window.confirm("Apagar esta conversa?")) return;
    try {
      await assistantApi.removeSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (sessionId === id) startNew();
    } catch {
      toast.error("Não foi possível apagar.");
    }
  };

  const submit = async (e, override) => {
    if (e) e.preventDefault();
    const question = String(override ?? input).trim();
    if (!question || sending) return;
    if (usage && usage.used_today >= usage.daily_limit) {
      toast.error(`Você usou suas ${usage.daily_limit} perguntas de hoje. Volte amanhã!`);
      return;
    }
    setSending(true);
    setInput("");
    // optimistic user message
    const optimistic = {
      id: `tmp_${Date.now()}`,
      role: "user",
      content: question,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    try {
      const res = await assistantApi.ask({ question, session_id: sessionId, mode });
      setSessionId(res.session_id);
      setMessages((m) => [
        ...m,
        {
          id: `a_${Date.now()}`,
          role: "assistant",
          content: res.answer,
          created_at: new Date().toISOString(),
        },
      ]);
      setUsage({
        used_today: res.used_today,
        daily_limit: res.daily_limit,
        date_key: usage?.date_key,
      });
      // Refresh sessions list (so the new one appears at the top)
      assistantApi.listSessions().then(setSessions).catch(() => {});
    } catch (err) {
      // revert optimistic message
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      const msg = formatApiError(err);
      toast.error(msg);
      // restore the input so the user can retry
      setInput(question);
    } finally {
      setSending(false);
    }
  };

  const remaining = useMemo(() => {
    if (!usage) return null;
    return Math.max(0, usage.daily_limit - usage.used_today);
  }, [usage]);

  // ---------------------------------------------------------------------------
  // Anonymous CTA
  // ---------------------------------------------------------------------------
  if (!isChecking && !isAuthenticated) {
    return (
      <div className="max-w-2xl mx-auto section-padding py-10" data-testid="assistant-page">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
          data-testid="assistant-back"
        >
          <ArrowLeft className="w-4 h-4" /> Início
        </Link>
        <div
          className="bg-[hsl(var(--parchment))] border border-gold/40 rounded-2xl p-8 sm:p-10 text-center"
          data-testid="assistant-anon-cta"
        >
          <div className="w-14 h-14 rounded-2xl bg-white/80 mx-auto flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-gold" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold mt-5">Auxiliar Bíblico, CM</p>
          <h1 className="font-display text-3xl sm:text-4xl text-navy mt-3 leading-tight">
            Crie sua conta gratuita para conversar
            <br />com o Assistente Bíblico
          </h1>
          <p className="text-navy/65 mt-4 max-w-md mx-auto text-sm">
            Receba respostas embasadas na Bíblia Almeida, com tom acolhedor e reverente.
            20 perguntas por dia, sem custo.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/cadastro?next=/assistente")}
              data-testid="assistant-anon-register"
              className="inline-flex items-center justify-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
            >
              Criar conta gratuita
            </button>
            <button
              type="button"
              onClick={() => navigate("/entrar?next=/assistente")}
              data-testid="assistant-anon-login"
              className="inline-flex items-center justify-center gap-2 bg-white border border-border text-navy hover:border-gold/60 px-6 py-3 rounded-full text-[11px] uppercase tracking-widest transition-colors"
            >
              Já tenho conta
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isChecking) {
    return (
      <div className="max-w-2xl mx-auto section-padding py-10">
        <p className="text-navy/55 text-sm" data-testid="assistant-checking">Carregando…</p>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Authenticated chat
  // ---------------------------------------------------------------------------
  const empty = messages.length === 0;
  const limitReached = usage && usage.used_today >= usage.daily_limit;

  return (
    <div className="max-w-3xl mx-auto section-padding py-6 sm:py-8" data-testid="assistant-page">
      <header className="flex items-center justify-between gap-3 mb-5">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy"
          data-testid="assistant-back"
        >
          <ArrowLeft className="w-4 h-4" /> Início
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowHistory((s) => !s)}
            data-testid="assistant-history-toggle"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-navy/60 hover:text-gold border border-border rounded-full px-3 py-1.5"
          >
            <MessagesSquare className="w-3.5 h-3.5" /> Histórico
          </button>
          <button
            type="button"
            onClick={startNew}
            data-testid="assistant-new"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-navy/60 hover:text-gold border border-border rounded-full px-3 py-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Nova
          </button>
        </div>
      </header>

      <div className="mb-5">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Auxiliar Bíblico, CM</p>
        <h1 className="font-display text-3xl sm:text-4xl text-navy mt-2">
          {empty ? `Olá, ${user?.name || "amigo(a)"}.` : "Conversa em andamento"}
        </h1>
        {usage && (
          <p
            className="text-[12px] text-navy/55 mt-2"
            data-testid="assistant-usage"
          >
            {usage.used_today}/{usage.daily_limit} perguntas usadas hoje · reseta à meia-noite
          </p>
        )}
      </div>

      {/* FASE 3, Seletor de modo + atalho Plano Semanal */}
      <div
        className="mb-5 -mx-1 flex items-center gap-2 overflow-x-auto pb-1 px-1"
        data-testid="assistant-modes"
      >
        {MODES.map((m) => {
          const Icon = m.Icon;
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              data-testid={`assistant-mode-${m.key}`}
              aria-pressed={active}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-[11px] uppercase tracking-widest transition-colors border ${
                active
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy/65 border-border hover:border-gold/60"
              }`}
              title={m.blurb}
            >
              <Icon className={`w-3.5 h-3.5 ${active ? "text-gold" : "text-navy/55"}`} />
              {m.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setWeeklyOpen(true)}
          data-testid="assistant-weekly-open"
          className="ml-auto inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-[11px] uppercase tracking-widest border border-gold/40 text-navy bg-gold/10 hover:bg-gold hover:text-navy transition-colors"
          title="Plano semanal de crescimento"
        >
          <CalendarRange className="w-3.5 h-3.5 text-gold" />
          Plano semanal
        </button>
      </div>
      <p
        className="text-[12px] text-navy/55 -mt-3 mb-5"
        data-testid="assistant-mode-blurb"
      >
        {MODES.find((m) => m.key === mode)?.blurb}
      </p>

      {weeklyOpen && (
        <WeeklyPlanModal onClose={() => setWeeklyOpen(false)} />
      )}

      {showHistory && (
        <div
          className="bg-white border border-border rounded-2xl p-4 mb-5"
          data-testid="assistant-history"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-navy/55 mb-3">Suas conversas</p>
          {sessions.length === 0 ? (
            <p className="text-navy/55 text-sm">Nenhuma conversa ainda.</p>
          ) : (
            <ul className="space-y-1.5">
              {sessions.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center gap-2"
                  data-testid={`assistant-history-${s.id}`}
                >
                  <button
                    type="button"
                    onClick={() => openSession(s.id)}
                    className="flex-1 text-left text-sm text-navy hover:text-gold truncate px-3 py-2 rounded-xl hover:bg-[hsl(var(--parchment))]"
                  >
                    {s.title}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeSession(s.id)}
                    aria-label="Apagar conversa"
                    data-testid={`assistant-history-remove-${s.id}`}
                    className="w-8 h-8 rounded-full text-navy/55 hover:text-red-500 flex items-center justify-center"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div
        ref={scrollerRef}
        className="bg-[hsl(var(--parchment))] border border-border rounded-2xl p-4 sm:p-5 min-h-[40vh] max-h-[55vh] overflow-y-auto"
        data-testid="assistant-thread"
      >
        {empty ? (
          <div className="py-6 sm:py-10">
            <p className="text-navy/65 text-sm max-w-md">
              Faça sua pergunta. Respostas embasadas na Bíblia Almeida (domínio público), com tom acolhedor.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-2.5" data-testid="assistant-suggestions">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => submit(null, s.text)}
                  data-testid={`assistant-suggestion-${i}`}
                  className="text-left bg-white border border-border hover:border-gold/60 rounded-xl px-4 py-3 text-sm text-navy/85 transition-colors"
                >
                  {s.text}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <ul className="space-y-4" data-testid="assistant-messages">
            {messages.map((m) => (
              <li
                key={m.id}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`assistant-msg-${m.role}`}
              >
                <div
                  className={[
                    "max-w-[85%] rounded-2xl px-4 py-3 text-[14.5px] leading-relaxed whitespace-pre-wrap",
                    m.role === "user"
                      ? "bg-navy text-white rounded-br-sm"
                      : "bg-white border border-border text-navy/90 rounded-bl-sm",
                  ].join(" ")}
                >
                  {m.content}
                  <div
                    className={`mt-1.5 text-[10px] tracking-widest uppercase flex items-center justify-between gap-2 ${
                      m.role === "user" ? "text-white/60" : "text-navy/45"
                    }`}
                  >
                    <span>{fmtTime(m.created_at)}</span>
                    {m.role === "assistant" && m.content && m.content.length > 40 && (
                      <div data-testid={`assistant-share-slot-${m.id}`} className="-my-2 -mr-2">
                        <ShareButton
                          contentType="devocional"
                          contentId={`assistant-${m.id}`}
                          title="Auxiliar Bíblico"
                          mainText={m.content}
                          theme="contemplacao"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
            {sending && (
              <li className="flex justify-start" data-testid="assistant-typing">
                <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-navy/55 inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Pensando…
                </div>
              </li>
            )}
          </ul>
        )}
      </div>

      <form
        onSubmit={submit}
        className="mt-4 flex items-end gap-2 sm:gap-3"
        data-testid="assistant-form"
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit(e);
            }
          }}
          placeholder={limitReached ? "Limite diário atingido, volte amanhã" : "Pergunte sobre a Bíblia, fé, dúvidas espirituais..."}
          rows={2}
          maxLength={2000}
          disabled={sending || limitReached}
          data-testid="assistant-input"
          className="flex-1 rounded-2xl border border-border bg-white p-3 text-sm text-navy/90 resize-none focus:outline-none focus:ring-2 focus:ring-gold/40 disabled:opacity-60"
        />
        <button
          type="submit"
          disabled={sending || !input.trim() || limitReached}
          aria-label="Enviar"
          data-testid="assistant-send"
          className="shrink-0 inline-flex items-center justify-center bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 w-12 h-12 rounded-full transition-colors"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>

      {limitReached && (
        <p className="text-center text-[12px] text-navy/55 mt-3" data-testid="assistant-limit">
          Você usou suas {usage.daily_limit} perguntas de hoje. Volte amanhã!
        </p>
      )}

      <p className="text-[11px] text-navy/45 mt-4 leading-relaxed">
        O Auxiliar não substitui aconselhamento pastoral. Para temas delicados, fale com o Pastor Carlos pelo
        <Link to="/oracao" className="underline hover:text-gold ml-1">WhatsApp de oração</Link>.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Weekly Plan modal, visualiza o plano da semana e permite (re)gerar
// ---------------------------------------------------------------------------
function WeeklyPlanModal({ onClose }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    let alive = true;
    assistantApi
      .weeklyPlan()
      .then((d) => alive && setPlan(d))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const p = await assistantApi.generateWeeklyPlan();
      setPlan(p);
      toast.success("Seu plano semanal está pronto!");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Não foi possível gerar agora. Tente novamente.",
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-navy/45 backdrop-blur-[2px] flex items-center justify-center p-4"
      role="dialog"
      aria-label="Plano semanal de crescimento"
      data-testid="weekly-plan-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 sm:p-6 border-b border-border flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
              Plano de crescimento
            </p>
            <h2 className="font-display text-2xl text-navy mt-1">Sua semana</h2>
            <p className="text-[12px] text-navy/55 mt-1">
              Personalizado pela IA com base na sua Jornada.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            data-testid="weekly-plan-close"
            className="w-8 h-8 rounded-full hover:bg-navy/5 flex items-center justify-center"
          >
            <X className="w-4 h-4 text-navy/55" />
          </button>
        </div>
        <div className="p-5 sm:p-6">
          {loading ? (
            <p className="text-sm text-navy/55">Carregando…</p>
          ) : !plan ? (
            <div className="text-center py-6">
              <CalendarRange className="w-10 h-10 mx-auto text-gold" />
              <p className="font-display text-xl text-navy mt-3">
                Você ainda não tem um plano desta semana.
              </p>
              <p className="text-sm text-navy/65 mt-2 max-w-xs mx-auto">
                Quanto mais você registrar na sua Jornada, mais personalizado
                o plano fica.
              </p>
              <button
                type="button"
                onClick={generate}
                disabled={generating}
                data-testid="weekly-plan-generate"
                className="mt-5 inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy disabled:opacity-50 px-6 py-3 rounded-full text-[11px] uppercase tracking-widest"
              >
                {generating ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                Gerar meu plano da semana
              </button>
            </div>
          ) : (
            <div className="space-y-5" data-testid="weekly-plan-content">
              <div className="rounded-2xl bg-[hsl(var(--parchment))] p-4">
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                  Versículo âncora · {plan.iso_week}
                </p>
                <p className="font-serif text-base text-navy mt-1.5 leading-relaxed">
                  “{plan.anchor_verse_text}”
                </p>
                <BibleRefLink
                  refText={plan.anchor_verse_ref}
                  className="text-[11px] uppercase tracking-widest text-navy/55 mt-2 inline-block hover:underline hover:text-gold transition-colors"
                />
              </div>
              {[
                { key: "spiritual_goal", label: "Espiritual" },
                { key: "health_goal", label: "Saúde" },
                { key: "family_goal", label: "Família" },
                { key: "purpose_goal", label: "Propósito" },
              ].map((g) => (
                <div key={g.key}>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-navy/55">
                    {g.label}
                  </p>
                  <p className="text-sm text-navy mt-1 leading-relaxed">
                    {plan[g.key]}
                  </p>
                </div>
              ))}
              <p className="text-sm text-navy/75 italic leading-relaxed border-t border-border pt-4">
                {plan.encouragement}
              </p>
              <button
                type="button"
                onClick={generate}
                disabled={generating}
                data-testid="weekly-plan-refresh"
                className="w-full inline-flex items-center justify-center gap-2 border border-border hover:border-gold/60 text-navy disabled:opacity-50 px-4 py-2.5 rounded-full text-[11px] uppercase tracking-widest"
              >
                {generating ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
                Gerar novamente
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
